import { Col, Form, FormInstance, Row } from 'antd';
import { useIntl } from 'react-intl';
import moment from 'moment';
import {
  CompaniesInfiniteSelectField,
  FloatingFormItem,
  InputDate,
} from '@/components/_common';
import {
  MaterialReturnListRequest,
  ReturnMaterialFormValues,
} from '@/core/domain/stock/MaterialReturn/MaterialReturn.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import { ExtraFiltersProps } from '@/types/ExtraFiltersProps';
import EmployeesInfiniteSelectField from '@/components/_common/EmployeesInfiniteSelectField';

interface Props extends ExtraFiltersProps<MaterialReturnListRequest> {
  form: FormInstance<ReturnMaterialFormValues>;
}

const ReturnMaterialForm: React.FC<Props> = ({
  form,
  queryParams,
  setQueryParams,
}) => {
  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const intl = useIntl();

  const returnerId = Form.useWatch('returnerId', form);
  const companyId = Form.useWatch('companyId', form);

  return (
    <Form layout="vertical" form={form}>
      <Row gutter={[16, 9]}>
        <Col xs={24} md={12} xl={6}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'deliveryOrReturnOfMaterials.returnMaterial.form.dateOfReturn',
            })}
            name="returnDate"
            initialValue={moment()}
            rules={[defaultEmptyFieldError]}
          >
            <InputDate />
          </FloatingFormItem>
        </Col>

        <Col xs={24} md={12} xl={6}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'deliveryOrReturnOfMaterials.returnMaterial.form.company',
            })}
            name="companyId"
            rules={[defaultEmptyFieldError]}
            getValueFromEvent={(value) => {
              form.resetFields(['receiverId', 'returnerId']);
              return value;
            }}
            initialValue={queryParams?.companyId}
          >
            <CompaniesInfiniteSelectField
              allowClear
              value={queryParams?.companyId}
              onChange={(value) =>
                setQueryParams((prev) => ({
                  ...prev,
                  companyId: value,
                }))
              }
              queryEnable
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} md={12} xl={6}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'deliveryOrReturnOfMaterials.returnMaterial.form.returnedBy',
            })}
            name="returnerId"
            rules={[defaultEmptyFieldError]}
            getValueFromEvent={(value) => {
              form.setFieldValue('receiverId', undefined);

              return value;
            }}
          >
            <EmployeesInfiniteSelectField
              queryFilter={{ companyId }}
              disabled={!companyId}
              selectSingle={false}
              allowClear
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} md={12} xl={6}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'deliveryOrReturnOfMaterials.returnMaterial.form.receivedBy',
            })}
            name="receiverId"
            rules={[defaultEmptyFieldError]}
          >
            <EmployeesInfiniteSelectField
              queryFilter={{ companyId }}
              disabled={!companyId}
              excludedId={returnerId}
              selectSingle={false}
              allowClear
            />
          </FloatingFormItem>
        </Col>
      </Row>
    </Form>
  );
};

export default ReturnMaterialForm;
